import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { authStore } from '@/store/auth'
import { CheckCircle2, Loader2 } from 'lucide-react'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = authStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [remember, setRemember] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const verified = params.get('verified')
    const reason = params.get('reason')
    const emailParam = params.get('email')
    if (emailParam) setEmail(emailParam)
    if (verified === '1') toast.success('Email verified! You can now sign in.')
    else if (verified === '0') toast.error('Verification failed' + (reason ? `: ${reason}` : ''))
  }, [location.search])

  // Carried onto the "Sign up" link below so an invitee who lands on /login
  // first (e.g. from a stale bookmark) doesn't lose their destination — the
  // same `next` Register.tsx forwards through VerifyEmail back to here.
  const next = useMemo(() => new URLSearchParams(location.search).get('next') || '', [location.search])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await login(email, password)
      toast.success('Welcome back!')
      // `next` always wins — RequireOrg (wrapping /app and /welcome) is the
      // authority on whether the destination is actually reachable, not this
      // page (plan 024 design decision 7). With no `next`, /welcome is a safe
      // default even for an org-less user: RequireOrg bounces them from there
      // to /setup-organization, so this fallback doesn't need to know about
      // org membership at all.
      const storeUser = authStore.getState().user
      if (next) navigate(next)
      else if (storeUser && !storeUser.onboarding_complete) navigate('/welcome')
      else navigate('/app')
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Invalid email or password'
      if (/verify/i.test(msg)) {
        toast.error('Please verify your email before signing in.', {
          action: { label: 'Resend', onClick: () => navigate(`/verify-email?email=${encodeURIComponent(email)}`) },
        })
      } else {
        toast.error(msg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gold-gradient p-12 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-white/20 backdrop-blur" />
          <span className="text-xl font-semibold">Granula</span>
        </div>
        <div className="relative z-10">
          <h1 className="max-w-sm text-4xl font-semibold leading-tight">
            Run your team's work, money and people in one place.
          </h1>
          <p className="mt-4 max-w-md text-white/80">
            Tasks, projects, leave and expenses — the operating system for modern African teams.
          </p>
          <ul className="mt-8 space-y-3">
            {['Projects & tasks that stay in sync', 'Leave & expense approvals built in', 'Roles & permissions for every tier'].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/90">
                <CheckCircle2 className="h-5 w-5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-sm text-white/60">© Granula · Work, organised.</p>
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="h-8 w-8 rounded-full bg-gold-gradient" />
            <span className="text-xl font-semibold">Granula</span>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight">Welcome back</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your workspace to continue.</p>

          <Button
            type="button"
            variant="outline"
            onClick={() => (window.location.href = '/api/auth/oauth/google/start')}
            className="mt-6 w-full"
          >
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-gold hover:underline">Forgot password?</Link>
              </div>
              <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} />
              <Label htmlFor="remember" className="text-sm text-muted-foreground">Remember me</Label>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</> : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center text-sm text-muted-foreground">
            <div>
              Don't have an account?{' '}
              <Link
                to={`/register${next ? `?next=${encodeURIComponent(next)}` : ''}`}
                className="font-medium text-gold hover:underline"
              >
                Sign up
              </Link>
            </div>
            <div><Link to="/login/otp" className="text-gold hover:underline">Sign in with a one-time code</Link></div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const Component = Login
